import type { RequestDetails } from './http-client.types';

/**
 * Класс описывает ошибку, связанную с HttpClient.
 * Должен использоваться для описания всех остальных ошибок связанных с HttpClient, но никогда не использоваться как отдельный экземпляр.
 */
export class HttpClientError extends Error {
  /**
   * Код ошибки.
   */
  public readonly code: number;

  /**
   * Создать экземпляр ошибки.
   *
   * @param message - сообщение об ошибке.
   * @param code - код ошибки.
   */
  protected constructor(message: string, code: number) {
    super(message);
    this.code = code;

    Object.setPrototypeOf(this, HttpClientError.prototype);
  }
}

/**
 * Класс описывает логическую ошибку HttpClient.
 */
export class LogicError extends HttpClientError {
  /**
   * Создать экземпляр ошибки.
   *
   * @param message - сообщение об ошибке.
   */
  public constructor(message: string) {
    super(message, 0);

    Object.setPrototypeOf(this, LogicError.prototype);
  }
}

/**
 * Класс описывает категоризированные ошибки.
 * Должен использоваться для описания всех остальных категоризированных ошибок, но никогда не использоваться как отдельный экземпляр.
 *
 * @extends HttpClientError
 */
export class ClassifiedError extends HttpClientError {
  /**
   * Детали запроса.
   */
  public readonly details: RequestDetails | null;

  /**
   * Создать экземпляр ошибки.
   *
   * @param message - сообщение об ошибке.
   * @param code - код ошибки.
   * @param details - детали запроса.
   */
  protected constructor(message: string, code: number, details: RequestDetails | null) {
    super(message, code);
    this.details = details;

    Object.setPrototypeOf(this, ClassifiedError.prototype);
  }
}

/**
 * Ошибка, которая не попала ни в одну из категорий.
 */
export class UntiledError extends ClassifiedError {
  /**
   * Создать экземпляр ошибки.
   *
   * @param message - сообщение об ошибке.
   * @param code - код ошибки.
   * @param details - детали запроса.
   */
  public constructor(message: string, code: number, details: RequestDetails | null) {
    super(message, code, details);

    Object.setPrototypeOf(this, UntiledError.prototype);
  }
}

/**
 * Класс описывает ошибку "Bad Request".
 */
export class BadRequestError extends ClassifiedError {
  /**
   * Создать экземпляр ошибки.
   *
   * @param message - сообщение об ошибке.
   * @param details - детали запроса.
   */
  public constructor(message: string = 'Bad Request!', details: RequestDetails | null = null) {
    super(message, 400, details);

    Object.setPrototypeOf(this, BadRequestError.prototype);
  }
}

/**
 * Класс описывает ошибку "Unauthorized".
 */
export class UnauthorizedError extends ClassifiedError {
  /**
   * Создать экземпляр ошибки.
   *
   * @param message - сообщение об ошибке.
   * @param details - детали запроса.
   */
  public constructor(message: string = 'Unauthorized!', details: RequestDetails | null = null) {
    super(message, 401, details);

    Object.setPrototypeOf(this, UnauthorizedError.prototype);
  }
}

/**
 * Класс описывает ошибку "Forbidden".
 */
export class ForbiddenError extends ClassifiedError {
  /**
   * Создать экземпляр ошибки.
   *
   * @param message - сообщение об ошибке.
   * @param details - детали запроса.
   */
  public constructor(message: string = 'Forbidden!', details: RequestDetails | null = null) {
    super(message, 403, details);

    Object.setPrototypeOf(this, ForbiddenError.prototype);
  }
}

/**
 * Класс описывает ошибку "Not Found".
 */
export class NotFoundError extends ClassifiedError {
  /**
   * Создать экземпляр ошибки.
   *
   * @param message - сообщение об ошибке.
   * @param details - детали запроса.
   */
  public constructor(message: string = 'Not Found!', details: RequestDetails | null = null) {
    super(message, 404, details);

    Object.setPrototypeOf(this, NotFoundError.prototype);
  }
}

/**
 * Класс описывает ошибку "Method Not Allowed".
 */
export class MethodNotAllowedError extends ClassifiedError {
  /**
   * Создать экземпляр ошибки.
   *
   * @param message - сообщение об ошибке.
   * @param details - детали запроса.
   */
  public constructor(message: string = 'Method Not Allowed!', details: RequestDetails | null = null) {
    super(message, 405, details);

    Object.setPrototypeOf(this, MethodNotAllowedError.prototype);
  }
}

/**
 * Класс описывает ошибку "Payload Too Large".
 */
export class PayloadTooLargeError extends ClassifiedError {
  /**
   * Создать экземпляр ошибки.
   *
   * @param message - сообщение об ошибке.
   * @param details - детали запроса.
   */
  public constructor(message: string = 'Payload Too Large!', details: RequestDetails | null = null) {
    super(message, 413, details);

    Object.setPrototypeOf(this, PayloadTooLargeError.prototype);
  }
}

/**
 * Класс описывает ошибку "Unsupported Media Type".
 */
export class UnsupportedMediaTypeError extends ClassifiedError {
  /**
   * Создать экземпляр ошибки.
   *
   * @param message - сообщение об ошибке.
   * @param details - детали запроса.
   */
  public constructor(message: string = 'Unsupported Media Type!', details: RequestDetails | null = null) {
    super(message, 415, details);

    Object.setPrototypeOf(this, UnsupportedMediaTypeError.prototype);
  }
}

/**
 * Класс описывает ошибку "Unprocessable Entity".
 */
export class UnprocessableEntityError extends ClassifiedError {
  /**
   * Создать экземпляр ошибки.
   *
   * @param message - сообщение об ошибке.
   * @param details - детали запроса.
   */
  public constructor(message: string = 'Unprocessable Entity!', details: RequestDetails | null = null) {
    super(message, 422, details);

    Object.setPrototypeOf(this, UnprocessableEntityError.prototype);
  }
}

/**
 * Класс описывает ошибку "Too Many Requests".
 */
export class TooManyRequestsError extends ClassifiedError {
  /**
   * Создать экземпляр ошибки.
   *
   * @param message - сообщение об ошибке.
   * @param details - детали запроса.
   */
  public constructor(message: string = 'Too Many Requests!', details: RequestDetails | null = null) {
    super(message, 429, details);

    Object.setPrototypeOf(this, TooManyRequestsError.prototype);
  }
}

/**
 * Класс описывает ошибку "Internal Server Error".
 */
export class InternalServerError extends ClassifiedError {
  /**
   * Создать экземпляр ошибки.
   *
   * @param message - сообщение об ошибке.
   * @param details - детали запроса.
   */
  public constructor(message: string = 'Internal Server Error!', details: RequestDetails | null = null) {
    super(message, 500, details);

    Object.setPrototypeOf(this, InternalServerError.prototype);
  }
}

/**
 * Класс описывает ошибку "Not Implemented".
 */
export class NotImplementedError extends ClassifiedError {
  /**
   * Создать экземпляр ошибки.
   *
   * @param message - сообщение об ошибке.
   * @param details - детали запроса.
   */
  public constructor(message: string = 'Not Implemented!', details: RequestDetails | null = null) {
    super(message, 501, details);

    Object.setPrototypeOf(this, NotImplementedError.prototype);
  }
}

/**
 * Класс описывает ошибку "Bad Gateway".
 */
export class BadGatewayError extends ClassifiedError {
  /**
   * Создать экземпляр ошибки.
   *
   * @param message - сообщение об ошибке.
   * @param details - детали запроса.
   */
  public constructor(message: string = 'Bad Gateway!', details: RequestDetails | null = null) {
    super(message, 502, details);

    Object.setPrototypeOf(this, BadGatewayError.prototype);
  }
}

/**
 * Класс описывает ошибку "Service Unavailable".
 */
export class ServiceUnavailableError extends ClassifiedError {
  /**
   * Создать экземпляр ошибки.
   *
   * @param message - сообщение об ошибке.
   * @param details - детали запроса.
   */
  public constructor(message: string = 'Service Unavailable!', details: RequestDetails | null = null) {
    super(message, 503, details);

    Object.setPrototypeOf(this, ServiceUnavailableError.prototype);
  }
}

/**
 * Класс описывает ошибку "Gateway Timeout".
 */
export class GatewayTimeoutError extends ClassifiedError {
  /**
   * Создать экземпляр ошибки.
   *
   * @param message - сообщение об ошибке.
   * @param details - детали запроса.
   */
  public constructor(message: string = 'Gateway Timeout!', details: RequestDetails | null = null) {
    super(message, 504, details);

    Object.setPrototypeOf(this, GatewayTimeoutError.prototype);
  }
}
